import { useState, useEffect, useCallback } from 'react'
import {
  Building2, Plus, Search, Loader2, MapPin, Mail, Phone, Users, X,
  PauseCircle, PlayCircle, Trash2, Pencil, UserMinus, UserPlus, Download,
  KeyRound, Wallet, Percent, Banknote,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  fetchCompanies, fetchCompany, createCompany, updateCompany, setCompanyStatus,
  deleteCompany, attachCollectorsToCompany, detachCollectorFromCompany, fetchCollectors,
  fetchCompanyAdmins, createCompanyAdmin, fetchCompanyEarnings,
  setCompanyAdminActive, resetCompanyAdminPassword, revokeCompanyAdmin,
  fetchCompanyBalance,
} from '../services/adminService'
import { useAuthStore } from '../store/authStore'
import { exportToCsv } from '../utils/exportCsv'
import ZonePicker from '../components/ZonePicker'
import type { Company, CompanyCollector } from '../types'
import { money } from '../utils/money'

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
]

const EMPTY_FORM = {
  name: '',
  registrationNumber: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  address: '',
  serviceZones: [] as string[],
  /** Held as a percentage in the form; sent as a 0-1 fraction. */
  commissionPercent: '0',
  notes: '',
}

type CompanyForm = typeof EMPTY_FORM

/** The extra reporting the detail endpoint returns alongside the company. */
interface CompanyDetail {
  last30Days: { total: number; completed: number; missed: number; active: number }
  averageRating: number
  totalRatings: number
}

interface CompanyAdminRow {
  id: string
  name: string
  email: string
  phone: string | null
  isActive: boolean
  /** True until the holder swaps the password we issued for one of their own. */
  mustChangePassword: boolean
  createdAt: string
}

interface CompanyEarnings {
  payRate: number
  commissionRate: number
  completedCount: number
  totalEarned: number
  totalPaidOut: number
  pendingPayouts: number
  companyShare: number
  collectorShare: number
  currency: string
}

const EMPTY_ADMIN_FORM = { name: '', email: '', phone: '', password: '' }


interface CompanyBalance {
  grossEarned: number
  paidToCollectors: number
  committedToCollectors: number
  paidToCompany: number
  committedToCompany: number
  availableToCompany: number
}

function toForm(company: Company): CompanyForm {
  return {
    name: company.name,
    registrationNumber: company.registrationNumber ?? '',
    contactName: company.contactName,
    contactEmail: company.contactEmail,
    contactPhone: company.contactPhone ?? '',
    address: company.address ?? '',
    serviceZones: company.serviceZones,
    commissionPercent: String(Math.round((company.commissionRate ?? 0) * 100)),
    notes: company.notes ?? '',
  }
}


function StatusPill({ status }: { status: string }) {
  const active = status === 'ACTIVE'
  return (
    <span
      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
        active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {active ? 'Active' : 'Suspended'}
    </span>
  )
}

function Field({
  label, value, onChange, placeholder, type = 'text', required = false, hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  required?: boolean
  hint?: string
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#6B7280] mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
      {hint && <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p>}
    </div>
  )
}

export default function Companies() {
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin)

  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Company | null>(null)
  const [form, setForm] = useState<CompanyForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const [manageId, setManageId] = useState<string | null>(null)
  const [manageCompany, setManageCompany] = useState<Company | null>(null)
  const [manageCollectors, setManageCollectors] = useState<CompanyCollector[]>([])
  const [manageDetail, setManageDetail] = useState<CompanyDetail | null>(null)
  const [manageLoading, setManageLoading] = useState(false)
  const [available, setAvailable] = useState<{ id: string; name: string; email: string }[]>([])
  const [selectedToAttach, setSelectedToAttach] = useState<string[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdminRow[]>([])
  const [earnings, setEarnings] = useState<CompanyEarnings | null>(null)
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN_FORM)
  const [adminFormOpen, setAdminFormOpen] = useState(false)
  const [creatingAdmin, setCreatingAdmin] = useState(false)
  /** Which login is mid-change, so only its own row shows as busy. */
  const [adminActionId, setAdminActionId] = useState<string | null>(null)
  const [balance, setBalance] = useState<CompanyBalance | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchCompanies({
        status: statusFilter || undefined,
        search: search.trim() || undefined,
        limit: 100,
      })
      setCompanies(data?.companies ?? [])
    } catch {
      toast.error('Failed to load companies')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, search])

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  const openCreate = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setFormOpen(true)
  }

  const openEdit = (company: Company) => {
    setEditing(company)
    setForm(toForm(company))
    setFormOpen(true)
  }

  const handleSave = async () => {
    if (!form.name.trim() || !form.contactName.trim() || !form.contactEmail.trim()) {
      toast.error('Name, contact person, and contact email are required')
      return
    }
    setSaving(true)
    try {
      const percent = Number(form.commissionPercent)
      const payload = {
        name: form.name.trim(),
        registrationNumber: form.registrationNumber.trim() || undefined,
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim() || undefined,
        address: form.address.trim() || undefined,
        serviceZones: form.serviceZones,
        commissionRate: Number.isFinite(percent)
          ? Math.min(Math.max(percent, 0), 100) / 100
          : 0,
        notes: form.notes.trim() || undefined,
      }
      if (editing) {
        await updateCompany(editing.id, payload)
        toast.success(`${payload.name} updated`)
      } else {
        await createCompany(payload)
        toast.success(`${payload.name} added`)
      }
      setFormOpen(false)
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to save company')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async (company: Company) => {
    const next = company.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'
    if (
      next === 'SUSPENDED' &&
      !confirm(`Suspend ${company.name}? Its collectors will stop receiving new pickup offers.`)
    ) {
      return
    }
    setBusyId(company.id)
    try {
      await setCompanyStatus(company.id, next)
      toast.success(`${company.name} ${next === 'ACTIVE' ? 'reinstated' : 'suspended'}`)
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to update status')
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (company: Company) => {
    if (!confirm(`Delete ${company.name}? Its collectors stay on the platform as independents.`)) return
    setBusyId(company.id)
    try {
      await deleteCompany(company.id)
      toast.success(`${company.name} deleted`)
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to delete company')
    } finally {
      setBusyId(null)
    }
  }

  const openManage = async (company: Company) => {
    setManageId(company.id)
    setManageLoading(true)
    setSelectedToAttach([])
    try {
      const [detail, unattached, admins, earned, balanceRes] = await Promise.all([
        fetchCompany(company.id),
        fetchCollectors({ companyId: 'none', status: 'APPROVED', limit: 100 }),
        fetchCompanyAdmins(company.id).catch(() => ({ admins: [] })),
        fetchCompanyEarnings(company.id).catch(() => null),
        fetchCompanyBalance(company.id).catch(() => null),
      ])
      setCompanyAdmins(admins?.admins ?? [])
      setEarnings(earned)
      setBalance(balanceRes?.balance ?? null)
      setManageCompany(detail?.company ?? company)
      setManageCollectors(detail?.company?.collectors ?? [])
      setManageDetail(
        detail
          ? {
              last30Days: detail.last30Days ?? { total: 0, completed: 0, missed: 0, active: 0 },
              averageRating: detail.averageRating ?? 0,
              totalRatings: detail.totalRatings ?? 0,
            }
          : null,
      )
      setAvailable(
        (unattached?.collectors ?? []).map((c: { id: string; name: string; email: string }) => ({
          id: c.id,
          name: c.name,
          email: c.email,
        })),
      )
    } catch {
      toast.error('Failed to load company details')
      setManageId(null)
    } finally {
      setManageLoading(false)
    }
  }

  const handleAttach = async () => {
    if (!manageId || selectedToAttach.length === 0) return
    setManageLoading(true)
    try {
      await attachCollectorsToCompany(manageId, selectedToAttach)
      toast.success(`${selectedToAttach.length} collector(s) attached`)
      const company = companies.find((c) => c.id === manageId)
      if (company) await openManage(company)
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to attach collectors')
      setManageLoading(false)
    }
  }

  const handleCreateAdmin = async () => {
    if (!manageId) return
    if (!adminForm.name.trim() || !adminForm.email.trim() || adminForm.password.length < 8) {
      toast.error('Name, email, and a password of at least 8 characters are required')
      return
    }
    // The server also needs a capital letter and a digit; say so here.
    if (!(/[A-Z]/.test(adminForm.password) && /[0-9]/.test(adminForm.password))) {
      toast.error('The password needs at least one capital letter and one number')
      return
    }
    setCreatingAdmin(true)
    try {
      await createCompanyAdmin(manageId, {
        name: adminForm.name.trim(),
        email: adminForm.email.trim(),
        phone: adminForm.phone.trim() || undefined,
        password: adminForm.password,
      })
      toast.success(`${adminForm.name.trim()} can now sign in to manage this company`)
      setAdminForm(EMPTY_ADMIN_FORM)
      setAdminFormOpen(false)
      const admins = await fetchCompanyAdmins(manageId).catch(() => ({ admins: [] }))
      setCompanyAdmins(admins?.admins ?? [])
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to create the company login')
    } finally {
      setCreatingAdmin(false)
    }
  }

  /** Re-read the logins after any change, so the list never shows a stale state. */
  const reloadCompanyAdmins = async () => {
    if (!manageId) return
    const admins = await fetchCompanyAdmins(manageId).catch(() => ({ admins: [] }))
    setCompanyAdmins(admins?.admins ?? [])
  }

  const runAdminAction = async (
    adminId: string,
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    setAdminActionId(adminId)
    try {
      await action()
      toast.success(successMessage)
      await reloadCompanyAdmins()
    } catch (err) {
      // The backend refuses the changes that would lock an operator out of their
      // own portal, and says which one — so surface its wording rather than a
      // generic failure.
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'That change could not be made')
    } finally {
      setAdminActionId(null)
    }
  }

  const handleToggleAdmin = (admin: CompanyAdminRow) =>
    runAdminAction(
      admin.id,
      () => setCompanyAdminActive(manageId!, admin.id, !admin.isActive),
      admin.isActive ? `${admin.name} can no longer sign in` : `${admin.name} can sign in again`,
    )

  const handleResetAdminPassword = (admin: CompanyAdminRow) => {
    const password = window.prompt(
      `New password for ${admin.name} (${admin.email}).

At least 8 characters with one uppercase letter and one number. They will be asked to choose their own the next time they sign in.`,
    )
    if (!password) return
    return runAdminAction(
      admin.id,
      () => resetCompanyAdminPassword(manageId!, admin.id, password),
      `New password issued for ${admin.name}`,
    )
  }

  const handleRevokeAdmin = (admin: CompanyAdminRow) => {
    if (
      !window.confirm(
        `Remove ${admin.name}'s access to this company?

Their account is kept — it is named in the audit trail — but it is switched off and detached, so it can no longer reach the portal.`,
      )
    ) {
      return
    }
    return runAdminAction(
      admin.id,
      () => revokeCompanyAdmin(manageId!, admin.id),
      `${admin.name} no longer has access`,
    )
  }

  const handleDetach = async (collectorId: string, name: string) => {
    if (!manageId) return
    setManageLoading(true)
    try {
      await detachCollectorFromCompany(manageId, collectorId)
      toast.success(`${name} detached`)
      const company = companies.find((c) => c.id === manageId)
      if (company) await openManage(company)
      load()
    } catch {
      toast.error('Failed to detach collector')
      setManageLoading(false)
    }
  }

  const totals = companies.reduce(
    (acc, c) => ({
      collectors: acc.collectors + (c.metrics?.collectors ?? 0),
      active: acc.active + (c.status === 'ACTIVE' ? 1 : 0),
      pickups: acc.pickups + (c.metrics?.totalPickups ?? 0),
    }),
    { collectors: 0, active: 0, pickups: 0 },
  )

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Companies', value: companies.length, icon: Building2, color: 'text-blue-600 bg-blue-50' },
          { label: 'Active', value: totals.active, icon: PlayCircle, color: 'text-green-700 bg-green-50' },
          { label: 'Collectors Employed', value: totals.collectors, icon: Users, color: 'text-purple-600 bg-purple-50' },
          { label: 'Pickups Handled', value: totals.pickups, icon: MapPin, color: 'text-orange-600 bg-orange-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${color.split(' ')[1]} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
            </div>
            <div>
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <p className="text-xl font-bold text-[#1A1A2E]">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={`px-4 py-2 text-xs font-semibold transition-colors ${
                statusFilter === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="pl-9 pr-3 py-2 w-64 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              if (!companies.length) return
              exportToCsv(
                ['Name', 'Registration', 'Contact', 'Email', 'Phone', 'Status', 'Zones', 'Commission', 'Collectors', 'Pickups', 'Completion'],
                companies.map((c) => [
                  c.name, c.registrationNumber ?? '', c.contactName, c.contactEmail,
                  c.contactPhone ?? '', c.status, c.serviceZones.join(' | '),
                  `${Math.round((c.commissionRate ?? 0) * 100)}%`,
                  String(c.metrics?.collectors ?? 0), String(c.metrics?.totalPickups ?? 0),
                  `${c.metrics?.completionRate ?? 0}%`,
                ]),
                'companies',
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-2 rounded-xl hover:bg-gray-200 transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} />
            Add Company
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : companies.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#6B7280]">No companies yet</p>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Add a waste operator to group its collectors and contract it to service zones.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {companies.map((company) => (
            <div key={company.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-[#1A1A2E] truncate">{company.name}</p>
                    <StatusPill status={company.status} />
                  </div>
                  {company.registrationNumber && (
                    <p className="text-[10px] text-[#9CA3AF] mt-0.5 font-mono">
                      Reg. {company.registrationNumber}
                    </p>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-primary-600" />
                </div>
              </div>

              <div className="space-y-1 text-xs text-[#6B7280]">
                <p className="flex items-center gap-1.5">
                  <Users size={12} /> {company.contactName}
                </p>
                <p className="flex items-center gap-1.5 truncate">
                  <Mail size={12} /> {company.contactEmail}
                </p>
                {company.contactPhone && (
                  <p className="flex items-center gap-1.5">
                    <Phone size={12} /> {company.contactPhone}
                  </p>
                )}
                {company.address && (
                  <p className="flex items-center gap-1.5 truncate" title={company.address}>
                    <MapPin size={12} /> {company.address}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-1">
                {company.serviceZones.length === 0 ? (
                  <span className="text-[10px] text-orange-500 italic">No service zones set</span>
                ) : (
                  company.serviceZones.slice(0, 4).map((zone) => (
                    <span key={zone} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                      {zone}
                    </span>
                  ))
                )}
                {company.serviceZones.length > 4 && (
                  <span className="text-[10px] text-[#9CA3AF]">+{company.serviceZones.length - 4} more</span>
                )}
              </div>

              <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
                {[
                  { label: 'Collectors', value: company.metrics?.collectors ?? 0, color: 'text-blue-600 bg-blue-50' },
                  { label: 'On Duty', value: company.metrics?.activeCollectors ?? 0, color: 'text-green-700 bg-green-50' },
                  { label: 'Completion', value: `${company.metrics?.completionRate ?? 0}%`, color: 'text-purple-600 bg-purple-50' },
                  { label: 'Commission', value: `${Math.round((company.commissionRate ?? 0) * 100)}%`, color: 'text-orange-600 bg-orange-50' },
                ].map(({ label, value, color }) => (
                  <div key={label} className={`rounded-xl p-2 text-center ${color.split(' ')[1]}`}>
                    <p className={`text-base font-bold ${color.split(' ')[0]}`}>{value}</p>
                    <p className={`text-[10px] font-medium ${color.split(' ')[0]} opacity-80`}>{label}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => openManage(company)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary-50 border border-primary-200 rounded-xl text-xs font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
                >
                  <Users size={13} />
                  Collectors
                </button>
                <button
                  onClick={() => openEdit(company)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-[#6B7280] hover:bg-gray-100 transition-colors"
                  title="Edit company"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => handleToggleStatus(company)}
                  disabled={busyId === company.id}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors disabled:opacity-50 ${
                    company.status === 'ACTIVE'
                      ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                      : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                  }`}
                  title={company.status === 'ACTIVE' ? 'Suspend company' : 'Reinstate company'}
                >
                  {company.status === 'ACTIVE' ? <PauseCircle size={13} /> : <PlayCircle size={13} />}
                </button>
                {isSuperAdmin && (
                  <button
                    onClick={() => handleDelete(company)}
                    disabled={busyId === company.id}
                    className="flex items-center justify-center px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                    title="Delete company"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {formOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-[#1A1A2E]">
                {editing ? `Edit ${editing.name}` : 'Add Company'}
              </h3>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280]"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <Field label="Company name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} required placeholder="Accra Waste Services Ltd" />
              <Field label="Registration number" value={form.registrationNumber} onChange={(v) => setForm((f) => ({ ...f, registrationNumber: v }))} placeholder="CS123456789" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact person" value={form.contactName} onChange={(v) => setForm((f) => ({ ...f, contactName: v }))} required placeholder="Ama Mensah" />
                <Field label="Contact phone" value={form.contactPhone} onChange={(v) => setForm((f) => ({ ...f, contactPhone: v }))} placeholder="+233..." />
              </div>
              <Field label="Contact email" type="email" value={form.contactEmail} onChange={(v) => setForm((f) => ({ ...f, contactEmail: v }))} required placeholder="ops@company.com" />
              <Field
                label="Address"
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                placeholder="Ring Road East, Accra"
                hint="Geocoded on save. If no service zones are given, the zone for this address is used."
              />
              <ZonePicker
                value={form.serviceZones}
                onChange={(zones) => setForm((f) => ({ ...f, serviceZones: zones }))}
                hint="Pickups inside these zones prefer this company's collectors when the engine ranks candidates."
              />
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">
                  Commission (% of collector earnings)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.commissionPercent}
                    onChange={(e) => setForm((f) => ({ ...f, commissionPercent: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 pr-9 text-sm text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Percent className="w-4 h-4 text-[#9CA3AF] absolute right-3 top-1/2 -translate-y-1/2" />
                </div>
                <p className="text-[10px] text-[#9CA3AF] mt-1">
                  Reporting only. Payouts still go to individual collectors; this records what the
                  company is owed from their earnings under its contract.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="Contract terms, fleet size, anything worth recording."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-primary-600 rounded-xl text-sm font-semibold text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing ? 'Save changes' : 'Add company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage collectors drawer */}
      {manageId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A2E]">
                  {manageCompany?.name ?? 'Company'} — collectors
                </h3>
                <p className="text-xs text-[#6B7280]">
                  {manageCollectors.length} collector{manageCollectors.length !== 1 ? 's' : ''} on this company
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setManageId(null)
                  setManageCompany(null)
                  setManageDetail(null)
                  setAdminFormOpen(false)
                  setAdminForm(EMPTY_ADMIN_FORM)
                }}
                className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280]"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {manageLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {manageDetail && (
                  <div>
                    <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                      Last 30 days
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
                      {[
                        { label: 'Pickups', value: manageDetail.last30Days.total, color: 'text-blue-600 bg-blue-50' },
                        { label: 'Completed', value: manageDetail.last30Days.completed, color: 'text-green-700 bg-green-50' },
                        { label: 'In flight', value: manageDetail.last30Days.active, color: 'text-purple-600 bg-purple-50' },
                        { label: 'Missed', value: manageDetail.last30Days.missed, color: 'text-red-600 bg-red-50' },
                        {
                          label: `Rating (${manageDetail.totalRatings})`,
                          value: manageDetail.totalRatings > 0 ? `${manageDetail.averageRating}★` : '—',
                          color: 'text-yellow-600 bg-yellow-50',
                        },
                      ].map(({ label, value, color }) => (
                        <div key={label} className={`rounded-xl p-2 text-center ${color.split(' ')[1]}`}>
                          <p className={`text-lg font-bold ${color.split(' ')[0]}`}>{value}</p>
                          <p className={`text-[10px] font-medium ${color.split(' ')[0]} opacity-80`}>{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">Current collectors</p>
                  {manageCollectors.length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] italic">Nobody is attached to this company yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {manageCollectors.map((collector) => (
                        <div key={collector.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#1A1A2E] truncate">{collector.name}</p>
                            <p className="text-[11px] text-[#6B7280] truncate">{collector.email}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${collector.isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {collector.isAvailable ? 'On duty' : 'Off duty'}
                            </span>
                            <span className="text-[10px] text-[#9CA3AF]">cap {collector.maxDailyPickups}/day</span>
                            <button
                              type="button"
                              onClick={() => handleDetach(collector.id, collector.name)}
                              className="flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <UserMinus size={11} />
                              Detach
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {earnings && (
                  <div>
                    <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                      Earnings
                    </p>
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
                      {[
                        { label: 'Completed', value: String(earnings.completedCount), color: 'text-blue-600 bg-blue-50' },
                        { label: 'Gross', value: money(earnings.totalEarned, earnings.currency), color: 'text-green-700 bg-green-50' },
                        { label: `Company (${Math.round(earnings.commissionRate * 100)}%)`, value: money(earnings.companyShare, earnings.currency), color: 'text-purple-600 bg-purple-50' },
                        { label: 'Collectors', value: money(earnings.collectorShare, earnings.currency), color: 'text-orange-600 bg-orange-50' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className={`rounded-xl p-2 text-center ${color.split(' ')[1]}`}>
                          <p className={`text-sm font-bold ${color.split(' ')[0]}`}>{value}</p>
                          <p className={`text-[10px] font-medium ${color.split(' ')[0]} opacity-80`}>{label}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] mt-1.5 flex items-center gap-1">
                      <Wallet size={10} />
                      Payouts settle per collector. The company share is a contractual figure, not a
                      balance held on the platform.
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                    Who gets paid
                  </p>
                  <div className="border border-gray-100 rounded-xl px-3 py-2 bg-[#F8FAF9]">
                    <p className="text-sm font-semibold text-[#1A1A2E] flex items-center gap-1.5">
                      <Banknote size={12} className="text-primary-600" />
                      The company is paid
                    </p>
                    <p className="text-[11px] text-[#6B7280] mt-0.5">
                      A collector who belongs to a company is paid through it. The company requests
                      payouts for the work its people did and settles with them itself. Collectors
                      can still draw anything they earned before joining.
                    </p>
                  </div>

                  {balance && (
                    <p className="text-[10px] text-[#9CA3AF] mt-2">
                      {money(balance.availableToCompany)} available to request
                      {balance.paidToCollectors + balance.committedToCollectors > 0
                        ? `, after ${money(balance.paidToCollectors + balance.committedToCollectors)} already paid to collectors individually`
                        : ''}
                      .
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide">
                      Company logins
                    </p>
                    <button
                      type="button"
                      onClick={() => setAdminFormOpen((open) => !open)}
                      className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 bg-primary-50 border border-primary-200 px-2 py-1 rounded-lg hover:bg-primary-100 transition"
                    >
                      <KeyRound size={11} />
                      {adminFormOpen ? 'Cancel' : 'Add login'}
                    </button>
                  </div>

                  {companyAdmins.length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] italic">
                      Nobody from this company can sign in yet. A login lets them see their own
                      collectors, pickups and earnings — and nothing else.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {companyAdmins.map((admin) => (
                        <div key={admin.id} className="border border-gray-100 rounded-xl px-3 py-2">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#1A1A2E] truncate">{admin.name}</p>
                              <p className="text-[11px] text-[#6B7280] truncate">{admin.email}</p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {admin.mustChangePassword && (
                                <span
                                  title="Still using the password an administrator issued. They cannot do anything else until they change it."
                                  className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                                >
                                  Password not set
                                </span>
                              )}
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${admin.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {admin.isActive ? 'Active' : 'Disabled'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-100">
                            <button
                              type="button"
                              onClick={() => handleToggleAdmin(admin)}
                              disabled={adminActionId === admin.id}
                              className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-[#374151] hover:bg-gray-50 transition disabled:opacity-50"
                            >
                              {admin.isActive ? 'Suspend' : 'Restore'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetAdminPassword(admin)}
                              disabled={adminActionId === admin.id}
                              className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-[#374151] hover:bg-gray-50 transition disabled:opacity-50"
                            >
                              Reset password
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRevokeAdmin(admin)}
                              disabled={adminActionId === admin.id}
                              className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50 ml-auto"
                            >
                              Remove access
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {adminFormOpen && (
                    <div className="mt-3 border border-gray-100 rounded-xl p-3 space-y-2 bg-[#F8FAF9]">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          value={adminForm.name}
                          onChange={(e) => setAdminForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="Full name"
                          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          value={adminForm.phone}
                          onChange={(e) => setAdminForm((f) => ({ ...f, phone: e.target.value }))}
                          placeholder="Phone (optional)"
                          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <input
                        type="email"
                        value={adminForm.email}
                        onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="Email"
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="password"
                        value={adminForm.password}
                        onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder="Temporary password"
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <p className="text-[10px] text-[#9CA3AF]">
                        At least 8 characters with one uppercase letter and one number. Hand it to
                        the operator — they are required to replace it with one of their own before
                        they can use the portal, so you will not keep knowing their password.
                      </p>
                      <button
                        type="button"
                        onClick={handleCreateAdmin}
                        disabled={creatingAdmin}
                        className="w-full bg-primary-600 text-white text-xs font-semibold py-2 rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                      >
                        {creatingAdmin ? 'Creating…' : 'Create login'}
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                    Attach independent collectors
                  </p>
                  {available.length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] italic">
                      Every approved collector already belongs to a company.
                    </p>
                  ) : (
                    <>
                      <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-100">
                        {available.map((collector) => (
                          <label key={collector.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={selectedToAttach.includes(collector.id)}
                              onChange={(e) =>
                                setSelectedToAttach((prev) =>
                                  e.target.checked
                                    ? [...prev, collector.id]
                                    : prev.filter((id) => id !== collector.id),
                                )
                              }
                              className="w-4 h-4 accent-primary-600"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#1A1A2E] truncate">{collector.name}</p>
                              <p className="text-[11px] text-[#6B7280] truncate">{collector.email}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleAttach}
                        disabled={selectedToAttach.length === 0}
                        className="mt-3 flex items-center gap-1.5 bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                      >
                        <UserPlus size={13} />
                        Attach {selectedToAttach.length > 0 ? `${selectedToAttach.length} ` : ''}collector
                        {selectedToAttach.length === 1 ? '' : 's'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
